// Single flat permission-key enum. Collapses what used to be four separate
// "rbac tables" (nav visibility, customer-action flags, worklist-tab
// visibility, and the one real PERMISSIONS/ROLE_PERMISSIONS matrix) into one
// vocabulary, reused identically by nav links, routes, and forms - so a
// feature's nav visibility and its actual route/form enforcement can never
// drift out of sync again (that drift is how Tier 0 #2's ExternalAdministrator
// admin-sub-route bypass happened).
export const PERMISSIONS = {
  // Route
  WORKLIST_ROUTE: "WORKLIST_ROUTE",
  PATIENT_ROUTE: "PATIENT_ROUTE",
  REPORT_ROUTE: "REPORT_ROUTE",
  TRANSFER_WOUND_ASSESSMENT_ROUTE: "TRANSFER_WOUND_ASSESSMENT_ROUTE",
  MERGE_WOUND_ASSESSMENT_ROUTE: "MERGE_WOUND_ASSESSMENT_ROUTE",
  ADMIN_ROUTE: "ADMIN_ROUTE",

  PATIENT: "PATIENT",
  ADD_PATIENT: "ADD_PATIENT",
  PATIENT_MANUAL_CREATE: "PATIENT_MANUAL_CREATE",
  ASSESSMENT_THREE_DOT_MENU: "ASSESSMENT_THREE_DOT_MENU",
  ASSESSMENT: "ASSESSMENT",
  CHARACTERISTIC: "CHARACTERISTIC",
  MEASUREMENT: "MEASUREMENT",
  MEASUREMENT_BUTTON: "MEASUREMENT_BUTTON",
  TREATMENT: "TREATMENT",
  TREATMENT_SEARCH: "TREATMENT_SEARCH",
  TREATMENT_ADD_INTERVENTION: "TREATMENT_ADD_INTERVENTION",
  NOTES: "NOTES",
  CUSTOM_LABEL: "CUSTOM_LABEL",
  SIGN: "SIGN",
  LOCK: "LOCK",
  EXT_SIGN_LOCK: "EXT_SIGN_LOCK",
  RE_EVAL: "RE_EVAL",

  // Catch-all admin flag - superseded by the granular ADMIN_* feature keys
  // below (Phase 3 of the RBAC consolidation migrated every form off this
  // key). No remaining call site reads it; kept only inside
  // LEGACY_PERMISSION_KEYS in roleCapabilities.ts until the final cleanup
  // commit removes it along with rbac.js. Do not add new usages.
  ADMIN: "ADMIN",

  USER_PROFILE: "USER_PROFILE",
  USER_PROFILE_UPDATE_PASSWORD: "USER_PROFILE_UPDATE_PASSWORD",

  // Buttons
  WOUND_ASSESSMENT_WITHOUT_PHOTO: "WOUND_ASSESSMENT_WITHOUT_PHOTO",
  WOUND_ASSESSMENT_NO_PHOTO_CAUSE_STEP: "WOUND_ASSESSMENT_NO_PHOTO_CAUSE_STEP",
  WOUND_ASSESSMENT_NEW_WOUND: "WOUND_ASSESSMENT_NEW_WOUND",
  RISK_ASSESSMENT: "RISK_ASSESSMENT",
  UNABLE_TO_ACCESS_WOUND: "UNABLE_TO_ACCESS_WOUND",
  REGISTER_PATIENT_FOR_MOBILE: "REGISTER_PATIENT_FOR_MOBILE",
  PATIENT_SUBMITTED_WOUND_PHOTO_MANAGE: "PATIENT_SUBMITTED_WOUND_PHOTO_MANAGE",
  ASSESSMENT_NOTE_CREATE: "ASSESSMENT_NOTE_CREATE",
  ASSESSMENT_NOTE_DELETE: "ASSESSMENT_NOTE_DELETE",
  ASSESSMENT_NOTE_EDIT: "ASSESSMENT_NOTE_EDIT",

  // Edit
  NOTIFICATION_EDIT: "NOTIFICATION_EDIT",
  ADT_EDIT: "ADT_EDIT",
  ANATOMY_EDIT: "ANATOMY_EDIT",
  CHARACTERISTIC_HISTORY_EDIT: "CHARACTERISTIC_HISTORY_EDIT",
  CHARACTERISTIC_TISSUE_EDIT: "CHARACTERISTIC_TISSUE_EDIT",
  CHARACTERISTIC_TOUCH_EDIT: "CHARACTERISTIC_TOUCH_EDIT",
  CHARACTERISTIC_CUSTOM_FORM_EDIT: "CHARACTERISTIC_CUSTOM_FORM_EDIT",
  CHARACTERISTIC_CLASSIFICATION_EDIT: "CHARACTERISTIC_CLASSIFICATION_EDIT",
  CHARACTERISTIC_PROGRESS_EDIT: "CHARACTERISTIC_PROGRESS_EDIT",
  MEASUREMENT_EDIT: "MEASUREMENT_EDIT",
  TREATMENT_EDIT: "TREATMENT_EDIT",

  // Customer admin
  CUSTOMER_SALES_AREA_EDIT: "CUSTOMER_SALES_AREA_EDIT",

  // --- New: granular /admin/* feature keys ---------------------------------
  // One per AdminRoutes.js sub-route, plus the 2 top-level admin-only routes
  // (platformanalytics, and the ADMIN_ROUTE wrapper itself). Replaces the
  // nav-only navRbac table in rbac.js and the ADMIN catch-all on forms -
  // the identical key now gates the nav link, the route, and the form.
  ADMIN_PRODUCTS: "ADMIN_PRODUCTS",
  ADMIN_PATIENTS: "ADMIN_PATIENTS", // NE1 WMS / Patient Management
  ADMIN_CUSTOMERS: "ADMIN_CUSTOMERS", // also serves as "Company Profile" for externals
  ADMIN_USERS: "ADMIN_USERS",
  ADMIN_SSO: "ADMIN_SSO",
  ADMIN_FORM_BUILDER: "ADMIN_FORM_BUILDER",
  ADMIN_IN_APP_MESSAGING: "ADMIN_IN_APP_MESSAGING", // Announcements
  ADMIN_MOBILE_APP_MGMT: "ADMIN_MOBILE_APP_MGMT", // Layouts
  ADMIN_CONTEXTUAL_SUPPORT: "ADMIN_CONTEXTUAL_SUPPORT",
  ADMIN_FORMULARY: "ADMIN_FORMULARY",
  ADMIN_FORMULARY_BEST_PRACTICE_EDIT: "ADMIN_FORMULARY_BEST_PRACTICE_EDIT",
  ADMIN_PATIENT_EDIT: "ADMIN_PATIENT_EDIT",
  ADMIN_SURVEY_RESULT: "ADMIN_SURVEY_RESULT",
  ADMIN_USAGE_ANALYTICS: "ADMIN_USAGE_ANALYTICS",
  ADMIN_PLATFORM_ANALYTICS: "ADMIN_PLATFORM_ANALYTICS",
  USAGE_ANALYTICS_CUSTOMER_GROUP_FILTER: "USAGE_ANALYTICS_CUSTOMER_GROUP_FILTER",
  ADMIN_LIST_BULK_DELETE: "ADMIN_LIST_BULK_DELETE",
  ADMIN_LIST_IMPORT: "ADMIN_LIST_IMPORT",

  // Section-level groupings menu.js uses to show/hide a whole admin-submenu
  // header (replaces rbacMasterSecNav's navDashboard/navMaster).
  ADMIN_DASHBOARD_SECTION: "ADMIN_DASHBOARD_SECTION",
  ADMIN_MASTER_DATA_SECTION: "ADMIN_MASTER_DATA_SECTION",

  CUSTOMER_PRIMARY_GROUP_ORG_EDIT: "CUSTOMER_PRIMARY_GROUP_ORG_EDIT",
  CUSTOMER_EXTENDED_DETAILS: "CUSTOMER_EXTENDED_DETAILS",

  // --- New: contextual (not purely role-based) assessment check ------------
  // Replaces rbac.js's assessmentRbac table. Deliberately kept separate from
  // the "isOwnCustomer" runtime fact - see roleCapabilities.ts for the
  // combined-check convention (hasPermission(...) && isOwnCustomer === 1).
  ASSESSMENT_EXTERNAL_OWN_CUSTOMER: "ASSESSMENT_EXTERNAL_OWN_CUSTOMER",
} as const;

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
